import { useMutation, useQuery } from '@apollo/client'
import { type MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'
import { Autocomplete, TextField } from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-pro'
import { type GridCellEditingApi } from '@mui/x-data-grid/models/api/gridEditingApi'
import {
    ProcessStatus,
    type GetProcessStatusOptionsQuery,
} from '__generated__/graphql'
import { type FC } from 'react'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import { useCandidateSearch } from 'recruiter/hooks'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'

interface ProcessStatusEditCellProps {
    /** A reference to the MUI DataGrid cell editing API. Allows this component to manipulate the DataGrid it lives in. */
    api: GridCellEditingApi
    /** The candidate's active process status to display. */
    currentProcessStatus?:
        | GetProcessStatusOptionsQuery['processStatuses'][number]
        | null
    /** An array (or a function that returns an array) that specifies which queries you want to refetch after this cell is successfully edited.  */
    refetchQueries?: MutationBaseOptions['refetchQueries']
    /** The roster ID of the candidate whose process status is being edited. */
    rosterCandidateId: string
    /** The ID of the row in the DataGrid that this cell belongs to. */
    rowId: string
    canEdit: boolean
    rosterId: string
}

const ProcessStatusEditCell: FC<ProcessStatusEditCellProps> = ({
    api,
    rosterId,
    currentProcessStatus,
    refetchQueries,
    rosterCandidateId,
    rowId,
    canEdit,
}) => {
    const toast = useToast()
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)
    const { refetch } = useCandidateSearch()
    const gridApi = useGridApiContext()

    const [updateProcessStatus] = useMutation(UpdateProcessStatus, {
        refetchQueries,
        onCompleted(data) {
            if (data.updateCandidateProcessStatus.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Candidate process status updated successfully',
                    severity: 'success',
                })

                api.stopCellEditMode({
                    field: 'processStatus',
                    id: rowId,
                })

                if (refetch) {
                    refetch()
                }
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updateCandidateProcessStatus.message ||
                        'Error updating process status',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating process status'
                ),
                severity: 'error',
            })
        },
    })

    return (
        <Autocomplete
            disabled={!canEdit}
            onChange={(event, processStatus) => {
                const timestamp = new Date().toISOString()

                updateProcessStatus({
                    variables: {
                        rosterId,
                        rosterCandidateIds: [rosterCandidateId],
                        processStatus:
                            processStatus?.status ?? ProcessStatus.NoStatus, // TODO: See if we can get process statuses as a back-end enum
                    },
                    onCompleted: () => {
                        /* 🔸 patch only this row in the grid */
                        const currentRowId = rowId?.toString()
                        if (currentRowId && gridApi?.current?.getRow) {
                            const existingRow: any =
                                gridApi.current.getRow(currentRowId)
                            if (existingRow) {
                                const updatedRow = {
                                    ...existingRow,
                                    rosterCandidate: {
                                        ...existingRow.rosterCandidate,
                                        candidateProcessStatus: {
                                            ...existingRow.rosterCandidate
                                                ?.candidateProcessStatus,
                                            updatedAt: timestamp,
                                            processStatus: processStatus,
                                        },
                                    },
                                }
                                gridApi.current.updateRows([updatedRow])
                            }
                        }
                    },
                })
            }}
            options={sortProcessStatusesBySummaryGroup(
                processStatusOptions?.processStatuses ?? []
            )}
            value={currentProcessStatus ?? null}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            groupBy={(option) => option.processStatusSummaryGroup?.label ?? ''}
            fullWidth
            sx={{
                '& .MuiFormControl-root': {
                    height: '100%',
                },
                '& .MuiInputBase-root': {
                    height: '100%',
                    borderRadius: '0px',
                },
            }}
            renderInput={(renderParams) => (
                <TextField
                    {...renderParams}
                    fullWidth
                    onKeyDown={(e) => {
                        if (e.key !== 'Escape') e.stopPropagation()
                    }}
                    inputProps={{
                        ...renderParams.inputProps,
                        height: '100%',
                    }}
                />
            )}
        />
    )
}

export default ProcessStatusEditCell
